import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSchedulePerformance, getBillingPerformance } from '../api';
import { SegmentedControl, Popover, PopoverHeader, RadioMenuItem } from './ui';

/* ──────────────────────────────────────────────────────────────────
 * Performance tab — read-only "are we on track?" view.
 *
 * Compares each project's BASELINE schedule + billing snapshot
 * (captured on first finalize) against current state. Two sub-views
 * (Schedule / Billing) toggled by an internal SegmentedControl.
 *
 * 2026-05-28: warm-cream + sage token redesign per the Claude Design
 * handoff at `~/Downloads/design_handoff_performance`. Replaces the
 * prior slate-blue + indigo file in-place. Both APIs unchanged:
 * `getSchedulePerformance(:projectId)` + `getBillingPerformance(:projectId)`.
 *
 * Exports:
 *   default `SchedulePerformanceTab`  — full Performance tab with
 *                                        sub-tab toggle (the new pattern)
 *   named   `BillingPerformanceSection` — preserved for compat with any
 *                                          stray imports; just renders
 *                                          the billing sub-view directly.
 * ────────────────────────────────────────────────────────────────── */

/* ─────────────────────── Formatters ─────────────────────── */

const fmt$ = (n, { signed = false } = {}) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  const sign = signed && v > 0 ? '+' : (v < 0 ? '-' : '');
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmt$abbrev = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};
const fmtPct = (n, dp = 1, { signed = false } = {}) => {
  const v = Number(n) || 0;
  const sign = signed && v > 0 ? '+' : '';
  return `${sign}${v.toFixed(dp)}%`;
};
const fmtDays = (n, { signed = true } = {}) => {
  const v = Number(n) || 0;
  const sign = signed && v > 0 ? '+' : '';
  return `${sign}${v}d`;
};
const fmtMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} '${String(y).slice(2)}`;
};
const fmtMonthLong = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return `${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][m - 1]} ${y}`;
};
const fmtDate = (iso) => {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/* ─────────────────────── Icons ─────────────────────── */

const RefreshIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const ChartIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
    <line x1="3" y1="20" x2="21" y2="20" />
  </svg>
);
const ArrowRightIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h13M13 6l6 6-6 6" />
  </svg>
);
const CaretDownIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const Spinner = ({ size = 12, color = 'currentColor' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: 99,
    border: `2px solid color-mix(in oklch, ${color} 30%, transparent)`,
    borderTopColor: color,
    animation: 'spin .8s linear infinite',
    flexShrink: 0,
  }} />
);

/* ─────────────────────── KPI Tile primitive ─────────────────────── */

const KpiTile = ({ label, value, sub, tone = 'neutral', valueColorOverride }) => {
  const isStringValue = typeof value === 'string';
  const numericValue = isStringValue ? null : Number(value);
  const muted = !isStringValue && (numericValue == null || numericValue === 0);

  const palette = {
    neutral: {
      bg: 'var(--bg-surface)',
      border: 'var(--border-subtle)',
      eyebrow: 'var(--text-3)',
      value: muted ? 'var(--text-4)' : 'var(--text-1)',
      sub: 'var(--text-3)',
    },
    success: {
      bg: 'var(--success-soft)',
      border: 'color-mix(in oklch, var(--success) 22%, transparent)',
      eyebrow: 'var(--success)',
      value: muted ? 'var(--text-4)' : 'var(--success)',
      sub: 'var(--success)',
    },
    warning: {
      bg: 'var(--warning-soft)',
      border: 'color-mix(in oklch, var(--warning) 22%, transparent)',
      eyebrow: 'var(--warning)',
      value: muted ? 'var(--text-4)' : 'var(--warning)',
      sub: 'var(--warning)',
    },
    danger: {
      bg: 'var(--danger-soft)',
      border: 'color-mix(in oklch, var(--danger) 22%, transparent)',
      eyebrow: 'var(--danger)',
      value: muted ? 'var(--text-4)' : 'var(--danger)',
      sub: 'var(--danger)',
    },
  }[tone];

  return (
    <div style={{
      flex: 1, minWidth: 160,
      padding: '14px 18px 16px',
      background: palette.bg,
      border: `1px solid ${palette.border}`,
      borderRadius: 12,
      boxShadow: 'var(--shadow-sm)',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600,
        color: palette.eyebrow,
        textTransform: 'uppercase', letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}>{label}</div>
      <div className="mono" style={{
        fontSize: 27.5, fontWeight: 600,
        color: valueColorOverride || palette.value,
        letterSpacing: '-0.025em', lineHeight: 1.1,
      }}>{value}</div>
      {sub && (
        <div style={{
          fontSize: 13, color: palette.sub,
          letterSpacing: '-0.005em',
        }}>{sub}</div>
      )}
    </div>
  );
};

/* ─────────────────────── Empty state (no baseline) ─────────────────────── */

const EmptyBaseline = ({ title, caption, ctaLabel = 'Go to Billing', onCta }) => (
  <div style={{
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 12,
    boxShadow: 'var(--shadow-sm)',
    padding: '48px 24px',
    textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  }}>
    <div style={{
      width: 52, height: 52, borderRadius: 14,
      background: 'var(--accent-soft)',
      color: 'var(--accent-text)',
      border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <ChartIcon />
    </div>
    <div style={{ fontSize: 16.5, fontWeight: 600, color: 'var(--text-1)' }}>
      {title}
    </div>
    <div style={{
      fontSize: 14, color: 'var(--text-3)',
      maxWidth: 420, lineHeight: 1.5,
    }}>{caption}</div>
    {onCta && (
      <button
        type="button"
        onClick={onCta}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          height: 32, padding: '0 14px',
          marginTop: 4,
          borderRadius: 8,
          fontSize: 14, fontWeight: 500, color: 'var(--text-2)',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-surface)'}
      >
        {ctaLabel} <ArrowRightIcon />
      </button>
    )}
  </div>
);

/* ─────────────────────── Section header (title + caption + refresh) ─────────────────────── */

const PerfSectionHeader = ({ title, caption, onRefresh, refreshing }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
    <div style={{ minWidth: 0 }}>
      <h2 style={{
        margin: 0, fontSize: 19.5, fontWeight: 600,
        color: 'var(--text-1)', letterSpacing: '-0.018em',
      }}>{title}</h2>
      {caption && (
        <div style={{ fontSize: 14, color: 'var(--text-3)', marginTop: 3 }}>
          {caption}
        </div>
      )}
    </div>
    {onRefresh && (
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          height: 32, padding: '0 12px', borderRadius: 8,
          fontSize: 14, fontWeight: 500,
          color: 'var(--text-2)',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          boxShadow: 'var(--shadow-xs)',
          cursor: refreshing ? 'wait' : 'pointer',
          opacity: refreshing ? 0.85 : 1,
          flexShrink: 0,
        }}
        onMouseEnter={(e) => { if (!refreshing) e.currentTarget.style.background = 'var(--bg-hover)'; }}
        onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-surface)'}
      >
        {refreshing ? <Spinner size={11} /> : <RefreshIcon />}
        Refresh
      </button>
    )}
  </div>
);

/* ─────────────────────── Schedule sub-view ─────────────────────── */

const VAR_COLS = '70px 1fr 90px 90px 70px 90px 90px 70px 110px';

const SORT_OPTIONS = [
  { key: 'variance', label: 'Variance (worst first)', cmp: (a, b) => (b.finish_variance_days || 0) - (a.finish_variance_days || 0) },
  { key: 'code',     label: 'Task code',              cmp: (a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }) },
  { key: 'name',     label: 'Task name',              cmp: (a, b) => (a.task_name || '').localeCompare(b.task_name || '') },
  { key: 'pct',      label: '% complete (high → low)', cmp: (a, b) => (b.current_pct || 0) - (a.current_pct || 0) },
];

const VarianceChip = ({ days }) => {
  if (days == null) return <span style={{ color: 'var(--text-4)', fontSize: 13.5 }}>–</span>;
  const isBehind = days > 0;
  const isAhead = days < 0;
  if (!isBehind && !isAhead) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '2px 8px', borderRadius: 999,
        fontSize: 12.5, fontWeight: 600,
        color: 'var(--text-3)',
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border-subtle)',
        letterSpacing: '-0.005em', whiteSpace: 'nowrap',
      }}>On time</span>
    );
  }
  const fg = isBehind ? 'var(--danger)' : 'var(--success)';
  const bg = isBehind ? 'var(--danger-soft)' : 'var(--success-soft)';
  return (
    <span className="mono" style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 999,
      fontSize: 12.5, fontWeight: 600,
      color: fg, background: bg,
      border: `1px solid color-mix(in oklch, ${fg} 22%, transparent)`,
      letterSpacing: '-0.02em', whiteSpace: 'nowrap',
    }}>
      {isBehind ? `+${days}d late` : `${days}d early`}
    </span>
  );
};

const FilterChip = ({ active, tone = 'neutral', label, count, onClick }) => {
  const palette = {
    neutral: {
      activeBg: 'var(--accent-soft)',
      activeFg: 'var(--accent-text)',
      activeRing: 'color-mix(in oklch, var(--accent) 22%, transparent)',
    },
    danger: {
      activeBg: 'var(--danger-soft)',
      activeFg: 'var(--danger)',
      activeRing: 'color-mix(in oklch, var(--danger) 22%, transparent)',
    },
    success: {
      activeBg: 'var(--success-soft)',
      activeFg: 'var(--success)',
      activeRing: 'color-mix(in oklch, var(--success) 22%, transparent)',
    },
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        height: 28, padding: '0 12px',
        borderRadius: 999,
        fontSize: 13.5, fontWeight: 500,
        color: active ? palette.activeFg : 'var(--text-3)',
        background: active ? palette.activeBg : 'var(--bg-surface-2)',
        border: `1px solid ${active ? palette.activeRing : 'var(--border-subtle)'}`,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background .12s, color .12s, border-color .12s',
      }}
    >
      <span>{label}</span>
      <span className="mono" style={{
        fontSize: 12.5,
        color: active ? palette.activeFg : 'var(--text-3)',
        opacity: active ? 0.85 : 1,
        letterSpacing: '-0.02em',
      }}>({count})</span>
    </button>
  );
};

const SchedulePerformanceContent = ({ projectId, onGoBilling }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('variance');
  const [sortOpen, setSortOpen] = useState(false);
  const [sortAnchor, setSortAnchor] = useState(null);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) setRefreshing(true); else setLoading(true);
      const d = await getSchedulePerformance(projectId);
      setData(d);
    } catch (e) { console.error(e); }
    finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
        padding: '60px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        color: 'var(--text-3)', fontSize: 14.5,
      }}>
        <Spinner size={18} color="var(--accent)" /> Loading schedule performance…
      </div>
    );
  }

  if (!data || !data.has_baseline) {
    return (
      <EmptyBaseline
        title="No baseline snapshot yet"
        caption="A baseline schedule snapshot is created when you generate your first billing period. Create a pay application in the Billing tab to establish the baseline, then come back here to track variance."
        ctaLabel="Go to Billing"
        onCta={onGoBilling}
      />
    );
  }

  const { comparison, summary } = data;

  // Filter
  const filtered = comparison.filter((row) => {
    const fv = row.finish_variance_days;
    if (filter === 'behind')  return fv != null && fv > 0;
    if (filter === 'ahead')   return fv != null && fv < 0;
    if (filter === 'ontrack') return fv === 0 || fv == null;
    return true;
  });

  // Sort
  const sortOpt = SORT_OPTIONS.find((o) => o.key === sort) || SORT_OPTIONS[0];
  const sorted = [...filtered].sort(sortOpt.cmp);

  return (
    <>
      <PerfSectionHeader
        title="Schedule Performance"
        caption={`Baseline vs current schedule — ${comparison.length} ${comparison.length === 1 ? 'task' : 'tasks'} tracked`}
        onRefresh={() => fetchData({ silent: true })}
        refreshing={refreshing}
      />

      {/* KPI tiles */}
      <div style={{ display: 'flex', gap: 12 }}>
        <KpiTile
          label="Behind schedule"
          value={summary.behind}
          sub="tasks finishing late"
          tone={summary.behind > 0 ? 'danger' : 'neutral'}
        />
        <KpiTile
          label="Ahead of schedule"
          value={summary.ahead}
          sub="tasks finishing early"
          tone={summary.ahead > 0 ? 'success' : 'neutral'}
        />
        <KpiTile
          label="On track"
          value={summary.onTrack}
          sub="meeting baseline finish"
          tone="neutral"
        />
        <KpiTile
          label="Average variance"
          value={fmtDays(summary.avgVariance, { signed: true })}
          sub={
            summary.avgVariance > 0 ? 'days late on average'
            : summary.avgVariance < 0 ? 'days early on average'
            : 'on baseline'
          }
          tone={summary.avgVariance > 0 ? 'danger' : summary.avgVariance < 0 ? 'success' : 'neutral'}
        />
      </div>

      {/* Filter chips + Sort */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <FilterChip
            active={filter === 'all'}
            label="All"
            count={comparison.length}
            onClick={() => setFilter('all')}
          />
          <FilterChip
            active={filter === 'behind'}
            tone="danger"
            label="Behind"
            count={summary.behind}
            onClick={() => setFilter('behind')}
          />
          <FilterChip
            active={filter === 'ahead'}
            tone="success"
            label="Ahead"
            count={summary.ahead}
            onClick={() => setFilter('ahead')}
          />
          <FilterChip
            active={filter === 'ontrack'}
            label="On track"
            count={summary.onTrack}
            onClick={() => setFilter('ontrack')}
          />
        </div>

        <button
          type="button"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setSortAnchor(rect);
            setSortOpen((v) => !v);
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            height: 28, padding: '0 10px',
            background: sort !== 'variance' ? 'var(--accent-soft)' : 'transparent',
            color: sort !== 'variance' ? 'var(--accent-text)' : 'var(--text-3)',
            border: `1px solid ${sort !== 'variance' ? 'color-mix(in oklch, var(--accent) 22%, transparent)' : 'var(--border-subtle)'}`,
            borderRadius: 6,
            fontSize: 13.5, fontWeight: 500,
            cursor: 'pointer', whiteSpace: 'nowrap',
            transition: 'background .12s, color .12s, border-color .12s',
          }}
        >
          {sort === 'variance' ? 'Sort' : `Sort: ${SORT_OPTIONS.find((o) => o.key === sort)?.label}`}
          <CaretDownIcon />
        </button>

        <Popover
          open={sortOpen}
          anchor={sortAnchor}
          onClose={() => setSortOpen(false)}
          minWidth={240}
          align="right"
        >
          <PopoverHeader title="Sort by" />
          {SORT_OPTIONS.map((opt) => (
            <RadioMenuItem
              key={opt.key}
              selected={sort === opt.key}
              label={opt.label}
              onClick={() => {
                setSort(opt.key);
                setSortOpen(false);
              }}
            />
          ))}
        </Popover>
      </div>

      {/* Variance table */}
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
      }}>
        <div style={{ maxHeight: 'calc(100vh - 420px)', minHeight: 240, overflow: 'auto' }}>
          {/* Sticky header */}
          <div style={{
            display: 'grid', gridTemplateColumns: VAR_COLS, columnGap: 12,
            height: 38, alignItems: 'center',
            padding: '0 16px',
            position: 'sticky', top: 0, zIndex: 3,
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            {['Code', 'Task', 'Baseline start', 'Baseline finish', 'Baseline %',
              'Current start', 'Current finish', 'Current %', 'Variance'].map((label, i) => (
              <div key={label} style={{
                fontSize: 12, fontWeight: 600,
                color: 'var(--text-3)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
                textAlign: [4, 7, 8].includes(i) ? 'right' : 'left',
                whiteSpace: 'nowrap',
              }}>{label}</div>
            ))}
          </div>

          {/* Body */}
          {sorted.length === 0 ? (
            <div style={{
              padding: '40px 20px', textAlign: 'center',
              color: 'var(--text-4)', fontSize: 14.5,
            }}>
              No tasks match the current filter.
            </div>
          ) : (
            sorted.map((row, idx) => {
              const fv = row.finish_variance_days;
              const isBehind = fv != null && fv > 0;
              const isAhead = fv != null && fv < 0;
              const stripe = isBehind ? 'var(--danger)' : isAhead ? 'var(--success)' : 'transparent';
              const finishColor = isBehind ? 'var(--danger)' : isAhead ? 'var(--success)' : 'var(--text-1)';
              const isLast = idx === sorted.length - 1;
              return (
                <div
                  key={row.task_id}
                  style={{
                    display: 'grid', gridTemplateColumns: VAR_COLS, columnGap: 12,
                    minHeight: 56, alignItems: 'center',
                    padding: '0 16px',
                    boxShadow: `inset 3px 0 0 ${stripe}`,
                    borderBottom: !isLast ? '1px solid var(--border-subtle)' : 'none',
                    background: 'var(--bg-surface)',
                    fontSize: 13.5, transition: 'background .12s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-surface)'}
                >
                  <div className="mono" style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--accent-text)', letterSpacing: '-0.02em' }}>
                    {row.task_code || '–'}
                  </div>
                  <div
                    title={row.task_name}
                    style={{
                      fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >{row.task_name}</div>
                  <div className="mono" style={{ fontSize: 13.5, color: 'var(--text-3)', letterSpacing: '-0.02em' }}>
                    {fmtDate(row.baseline_start)}
                  </div>
                  <div className="mono" style={{ fontSize: 13.5, color: 'var(--text-3)', letterSpacing: '-0.02em' }}>
                    {fmtDate(row.baseline_finish)}
                  </div>
                  <div className="mono" style={{ fontSize: 13.5, color: 'var(--text-3)', textAlign: 'right', letterSpacing: '-0.02em' }}>
                    {row.baseline_pct != null ? fmtPct(row.baseline_pct) : '–'}
                  </div>
                  <div className="mono" style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
                    {fmtDate(row.current_start)}
                  </div>
                  <div className="mono" style={{ fontSize: 13.5, fontWeight: 500, color: finishColor, letterSpacing: '-0.02em' }}>
                    {fmtDate(row.current_finish)}
                  </div>
                  <div className="mono" style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-1)', textAlign: 'right', letterSpacing: '-0.02em' }}>
                    {row.current_pct != null ? fmtPct(row.current_pct) : '–'}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <VarianceChip days={fv} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

/* ─────────────────────── Billing sub-view ─────────────────────── */

const MONTH_COLS = '110px 1fr 1fr 1fr 1fr 1fr 1fr';

const useContainerWidth = (ref, fallback = 700) => {
  const [w, setW] = useState(fallback);
  useEffect(() => {
    if (!ref.current) return undefined;
    const measure = () => { const cw = ref.current?.clientWidth; if (cw && cw > 100) setW(cw); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return w;
};

const BillingMonthlyChart = ({ months, hoveredMonth, setHoveredMonth }) => {
  const ref = useRef(null);
  const cW = useContainerWidth(ref);

  const P = { t: 20, r: 16, b: 36, l: 56 };
  const H = 280;
  const colWidth = 86;
  const W = Math.max(months.length * colWidth, cW);
  const iW = W - P.l - P.r;
  const iH = H - P.t - P.b;
  const gap = iW / Math.max(months.length, 1);
  const bW = 22;

  const maxVal = Math.max(
    ...months.map((m) => Math.max(m.projected || 0, m.actual || 0)),
    1,
  ) * 1.15;

  const hovered = hoveredMonth ? months.findIndex((m) => m.month === hoveredMonth) : -1;
  const hoveredMonthObj = hovered >= 0 ? months[hovered] : null;

  return (
    <div ref={ref} style={{ position: 'relative', overflowX: 'auto', overflowY: 'visible' }}>
      <svg
        width={Math.max(W, cW)}
        height={H}
        viewBox={`0 0 ${Math.max(W, cW)} ${H}`}
        preserveAspectRatio="xMinYMid meet"
        style={{ display: 'block', minWidth: months.length > 8 ? W : 'auto' }}
      >
        <defs>
          <linearGradient id="perfProjBar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="var(--accent)" stopOpacity="1" />
            <stop offset="100%" stopColor="var(--accent-hover)" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="perfActBar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="var(--success)" stopOpacity="1" />
            <stop offset="100%" stopColor="var(--success)" stopOpacity="0.7" />
          </linearGradient>
        </defs>

        {/* Grid lines + Y-axis tick labels */}
        {Array.from({ length: 6 }, (_, i) => {
          const y = P.t + iH - (i / 5) * iH;
          const v = (i / 5) * maxVal;
          return (
            <g key={i}>
              <line
                x1={P.l} x2={W - P.r} y1={y} y2={y}
                stroke={i === 0 ? 'var(--border-default)' : 'var(--border-subtle)'}
                strokeWidth="1"
              />
              <text
                x={P.l - 10} y={y + 4}
                textAnchor="end"
                fill="var(--text-3)"
                fontSize="10"
                fontFamily="var(--font-mono)"
                style={{ letterSpacing: '-0.02em' }}
              >{fmt$abbrev(v)}</text>
            </g>
          );
        })}

        {/* Bars per month */}
        {months.map((m, i) => {
          const cx = P.l + i * gap + gap / 2;
          const pH = ((m.projected || 0) / maxVal) * iH;
          const aH = ((m.actual || 0) / maxVal) * iH;
          const baseY = P.t + iH;
          const isHovered = hovered === i;
          const anyHovered = hovered !== -1;
          const opacityFactor = anyHovered ? (isHovered ? 1 : 0.45) : 0.9;

          return (
            <g
              key={m.month}
              onMouseEnter={() => setHoveredMonth(m.month)}
              onMouseLeave={() => setHoveredMonth(null)}
              style={{ cursor: 'pointer' }}
            >
              {/* Column hover overlay */}
              <rect
                x={cx - gap / 2} y={P.t}
                width={gap} height={iH}
                fill={isHovered ? 'color-mix(in oklch, var(--accent-soft) 50%, transparent)' : 'transparent'}
              />
              {/* Projected bar */}
              <rect
                x={cx - bW - 1} y={baseY - pH}
                width={bW} height={Math.max(pH, 0)}
                rx="4" fill="url(#perfProjBar)"
                opacity={opacityFactor}
              />
              {/* Actual bar */}
              {(m.actual || 0) > 0 && (
                <rect
                  x={cx + 1} y={baseY - aH}
                  width={bW} height={Math.max(aH, 0)}
                  rx="4" fill="url(#perfActBar)"
                  opacity={opacityFactor}
                />
              )}
              {/* Month label */}
              <text
                x={cx} y={baseY + 20}
                textAnchor="middle"
                fill={isHovered ? 'var(--text-1)' : 'var(--text-3)'}
                fontSize="10"
                fontWeight={isHovered ? 600 : 500}
                fontFamily="var(--font-sans)"
                style={{ letterSpacing: '-0.005em' }}
              >{fmtMonth(m.month)}</text>
            </g>
          );
        })}
      </svg>

      {/* Tooltip — positioned via absolute over the hovered column */}
      {hoveredMonthObj && (() => {
        const i = hovered;
        const cx = P.l + i * gap + gap / 2;
        const pH = ((hoveredMonthObj.projected || 0) / maxVal) * iH;
        const aH = ((hoveredMonthObj.actual || 0) / maxVal) * iH;
        const topY = P.t + iH - Math.max(pH, aH) - 12;
        const variance = hoveredMonthObj.variance || 0;
        const retainage = hoveredMonthObj.retainageHeld || 0;
        return (
          <div style={{
            position: 'absolute',
            left: cx, top: topY,
            transform: 'translate(-50%, -100%)',
            minWidth: 220,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-md), 0 16px 32px -8px oklch(0.2 0.01 100 / 0.18)',
            padding: '10px 12px 12px',
            zIndex: 10,
            pointerEvents: 'none',
          }}>
            <div style={{
              position: 'absolute', bottom: -5, left: '50%',
              width: 8, height: 8,
              background: 'var(--bg-surface)',
              borderRight: '1px solid var(--border-default)',
              borderBottom: '1px solid var(--border-default)',
              transform: 'translateX(-50%) rotate(45deg)',
            }} />
            <div style={{
              fontSize: 11.5, fontWeight: 600,
              color: 'var(--text-3)',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              marginBottom: 6,
            }}>{fmtMonthLong(hoveredMonthObj.month)}</div>
            <TooltipRow color="var(--accent)" label="Projected" value={fmt$(hoveredMonthObj.projected)} />
            <TooltipRow color="var(--success)" label="Actual" value={fmt$(hoveredMonthObj.actual)} />
            {retainage > 0 && (
              <TooltipRow color="var(--warning)" label="Retainage" value={fmt$(retainage)} valueColor="var(--warning)" />
            )}
            <div style={{ height: 1, background: 'var(--border-subtle)', margin: '6px 0 4px' }} />
            <TooltipRow
              label="Variance"
              value={(Number(hoveredMonthObj.actual) || 0) === 0 ? '—' : fmt$(variance, { signed: true })}
              valueColor={
                (Number(hoveredMonthObj.actual) || 0) === 0 ? 'var(--text-4)'
                : variance >= 0 ? 'var(--success)'
                : 'var(--danger)'
              }
              bold
            />
          </div>
        );
      })()}
    </div>
  );
};

const TooltipRow = ({ label, value, color, valueColor, bold = false }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 12, padding: '2px 0',
  }}>
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-3)' }}>
      {color && (
        <span style={{
          width: 8, height: 8, borderRadius: 2,
          background: color, flexShrink: 0,
        }} />
      )}
      {label}
    </div>
    <span className="mono" style={{
      fontSize: 13.5,
      fontWeight: bold ? 700 : 500,
      color: valueColor || 'var(--text-1)',
      letterSpacing: '-0.02em',
    }}>{value}</span>
  </div>
);

const BillingPerformanceContent = ({ projectId, onGoBilling }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredMonth, setHoveredMonth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const d = await getBillingPerformance(projectId);
        if (!cancelled) setData(d);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading && !data) {
    return (
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
        padding: '60px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        color: 'var(--text-3)', fontSize: 14.5,
      }}>
        <Spinner size={18} color="var(--accent)" /> Loading billing performance…
      </div>
    );
  }

  if (!data || !data.has_baseline) {
    return (
      <EmptyBaseline
        title="No billing baseline yet"
        caption="The billing baseline is established from your first billing period's schedule snapshot. Create and finalize a pay application to start tracking billing performance."
        ctaLabel="Go to Billing"
        onCta={onGoBilling}
      />
    );
  }

  const { months = [], summary = {} } = data;
  const variance = Number(summary.totalVariance) || 0;
  const accuracy = (Number(summary.totalProjected) || 0) > 0
    ? Math.max(0, Math.round((1 - Math.abs(variance) / summary.totalProjected) * 100))
    : 0;

  return (
    <>
      <PerfSectionHeader
        title="Billing Performance"
        caption={`Baseline vs actual billing — ${months.length} ${months.length === 1 ? 'month' : 'months'} tracked`}
      />

      {/* KPI tiles */}
      <div style={{ display: 'flex', gap: 12 }}>
        <KpiTile
          label="Original projected"
          value={fmt$(summary.totalProjected)}
          sub="baseline target"
          tone="neutral"
          valueColorOverride={(Number(summary.totalProjected) || 0) > 0 ? 'var(--accent-text)' : undefined}
        />
        <KpiTile
          label="Actual billed"
          value={fmt$(summary.totalActual)}
          sub={
            (Number(summary.totalRetainage) || 0) > 0
              ? `+${fmt$(summary.totalRetainage)} retainage held`
              : 'no retainage held'
          }
          tone={(Number(summary.totalActual) || 0) > 0 ? 'success' : 'neutral'}
        />
        {/* Total variance tone stays neutral until actual billing has
            happened — otherwise an early-stage project (actual = $0
            on a long baseline) flips the tile to danger just because
            it hasn't billed yet, which reads as "we're behind" when
            it's really just "we haven't started billing the whole
            project". Same for the row tints + variance chip below. */}
        <KpiTile
          label="Total variance"
          value={fmt$(variance, { signed: true })}
          sub={(Number(summary.totalActual) || 0) === 0
            ? 'no billing yet to compare'
            : `${fmtPct(summary.variancePct || 0, 1, { signed: true })} vs baseline`}
          tone={
            (Number(summary.totalActual) || 0) === 0 ? 'neutral'
            : variance >= 0 ? 'success'
            : 'danger'
          }
        />
        <KpiTile
          label="Billing accuracy"
          value={`${accuracy}%`}
          sub={`${summary.monthsOverBudget || 0} over · ${summary.monthsUnderBudget || 0} under`}
          tone="neutral"
        />
      </div>

      {/* Monthly bar chart */}
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
        padding: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
          <div style={{
            fontSize: 15.5, fontWeight: 600,
            color: 'var(--text-1)', letterSpacing: '-0.01em',
          }}>Monthly: Projected vs Actual</div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <LegendDot color="var(--accent)" label="Projected" />
            <LegendDot color="var(--success)" label="Actual" />
          </div>
        </div>
        <BillingMonthlyChart
          months={months}
          hoveredMonth={hoveredMonth}
          setHoveredMonth={setHoveredMonth}
        />
      </div>

      {/* Month-by-month table */}
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto' }}>
          {/* Header */}
          <div style={{
            display: 'grid', gridTemplateColumns: MONTH_COLS, columnGap: 12,
            height: 38, alignItems: 'center',
            padding: '0 16px',
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            {[
              { label: 'Month', align: 'left' },
              { label: 'Projected', align: 'right' },
              { label: 'Actual', align: 'right' },
              { label: 'Variance', align: 'right' },
              { label: 'Cumulative projected', align: 'right', dividerLeft: true },
              { label: 'Cumulative actual', align: 'right' },
              { label: 'Cumulative variance', align: 'right' },
            ].map((c) => (
              <div key={c.label} style={{
                fontSize: 12, fontWeight: 600,
                color: 'var(--text-3)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
                textAlign: c.align,
                borderLeft: c.dividerLeft ? '1px solid var(--border-subtle)' : 'none',
                paddingLeft: c.dividerLeft ? 12 : 0,
              }}>{c.label}</div>
            ))}
          </div>
          {/* Rows */}
          {months.map((m, idx) => {
            const v = m.variance || 0;
            const cv = m.cumVariance || 0;
            const monthActual = Number(m.actual) || 0;
            const monthCumActual = Number(m.cumActual) || 0;
            // Only tint rows + color the variance cells when there's
            // been ACTUAL billing for the month (or cumulatively).
            // Otherwise an unbilled future month flashes red just for
            // being unbilled, which reads as a problem when it isn't.
            const hasMonthActual = monthActual > 0;
            const hasCumActual = monthCumActual > 0;
            const rowBg = !hasMonthActual
              ? 'var(--bg-surface)'
              : v > 0
                ? 'color-mix(in oklch, var(--success-soft) 60%, var(--bg-surface))'
                : v < 0
                  ? 'color-mix(in oklch, var(--danger-soft) 60%, var(--bg-surface))'
                  : 'var(--bg-surface)';
            const isLast = idx === months.length - 1;
            const vColor = !hasMonthActual ? 'var(--text-4)'
              : v > 0 ? 'var(--success)'
              : v < 0 ? 'var(--danger)'
              : 'var(--text-4)';
            const cvColor = !hasCumActual ? 'var(--text-4)'
              : cv > 0 ? 'var(--success)'
              : cv < 0 ? 'var(--danger)'
              : 'var(--text-4)';
            return (
              <div key={m.month} style={{
                display: 'grid', gridTemplateColumns: MONTH_COLS, columnGap: 12,
                minHeight: 56, alignItems: 'center',
                padding: '0 16px',
                borderBottom: !isLast ? '1px solid var(--border-subtle)' : 'none',
                background: rowBg,
              }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)' }}>
                  {fmtMonth(m.month)}
                </div>
                <div className="mono" style={{ fontSize: 14, color: 'var(--text-1)', textAlign: 'right', letterSpacing: '-0.02em' }}>
                  {fmt$(m.projected)}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="mono" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
                    {fmt$(m.actual)}
                  </div>
                  {(Number(m.retainageHeld) || 0) > 0 && (
                    <div className="mono" style={{ fontSize: 12, color: 'var(--warning)', marginTop: 1, letterSpacing: '-0.02em' }}>
                      +{fmt$(m.retainageHeld)} retainage
                    </div>
                  )}
                </div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: vColor, textAlign: 'right', letterSpacing: '-0.02em' }}>
                  {!hasMonthActual || v === 0 ? '—' : fmt$(v, { signed: true })}
                </div>
                <div className="mono" style={{
                  fontSize: 14, color: 'var(--text-2)', textAlign: 'right',
                  letterSpacing: '-0.02em',
                  borderLeft: '1px solid var(--border-subtle)', paddingLeft: 12,
                }}>
                  {fmt$(m.cumProjected)}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="mono" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)', letterSpacing: '-0.02em' }}>
                    {fmt$(m.cumActual)}
                  </div>
                  {(Number(m.cumRetainage) || 0) > 0 && (
                    <div className="mono" style={{ fontSize: 12, color: 'var(--warning)', marginTop: 1, letterSpacing: '-0.02em' }}>
                      +{fmt$(m.cumRetainage)} retainage
                    </div>
                  )}
                </div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: cvColor, textAlign: 'right', letterSpacing: '-0.02em' }}>
                  {!hasCumActual || cv === 0 ? '—' : fmt$(cv, { signed: true })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

const LegendDot = ({ color, label }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span style={{
      width: 10, height: 10, borderRadius: 3,
      background: color, flexShrink: 0,
    }} />
    <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)' }}>{label}</span>
  </div>
);

/* ─────────────────────── Page assembly ─────────────────────── */

const SchedulePerformanceTab = ({ projectId }) => {
  const [perfView, setPerfView] = useState('schedule');

  // Inject spin keyframe once (used by Spinner). The Schedule + Rollup
  // tabs already inject `@keyframes spin`/`rollupPulse` defensively;
  // mirror the same pattern here so the Performance tab works whether
  // or not those tabs have mounted.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const id = 'perf-anim';
    if (document.getElementById(id)) return undefined;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(s);
    return () => {};
  }, []);

  const goBilling = useCallback(() => {
    // Best-effort: switch to the Billing project tab via URL hash. Falls
    // back to a no-op if the URL convention doesn't apply (e.g., when
    // the component is embedded elsewhere).
    try {
      const m = window.location.pathname.match(/(\/projects\/[^/]+\/)/);
      if (m) window.location.assign(`${m[1]}billing`);
    } catch (_e) { /* ignore */ }
  }, []);

  return (
    <div style={{
      padding: '20px 24px 24px',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <SegmentedControl
        value={perfView}
        onChange={setPerfView}
        options={[
          { value: 'schedule', label: 'Schedule' },
          { value: 'billing',  label: 'Billing'  },
        ]}
      />
      {perfView === 'schedule' && (
        <SchedulePerformanceContent projectId={projectId} onGoBilling={goBilling} />
      )}
      {perfView === 'billing' && (
        <BillingPerformanceContent projectId={projectId} onGoBilling={goBilling} />
      )}
    </div>
  );
};

export default SchedulePerformanceTab;

// Preserved for compatibility with any stray imports — renders only the
// Billing sub-view body (no sub-tab toggle, no Schedule). ProjectDetail
// no longer imports this; if you do, pass `projectId` as a prop.
export const BillingPerformanceSection = ({ projectId }) => (
  <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
    <BillingPerformanceContent projectId={projectId} onGoBilling={() => {}} />
  </div>
);

